import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Save, Search, Trash2, X } from "lucide-react";
import DashboardShell from "@/components/dashboard/DashboardShell";

const configurations = {
  accounts: {
    title: "Konten",
    subtitle: "Geschäfts- und Verrechnungskonten verwalten",
    singular: "Konto",
    fields: [
      { key: "name", label: "Kontoname", required: true },
      { key: "iban", label: "IBAN" },
      {
        key: "currency",
        label: "Währung",
        type: "select",
        options: ["EUR", "USD", "GBP", "CHF"],
      },
      { key: "balance", label: "Saldo" },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["Aktiv", "Gesperrt", "In Prüfung"],
      },
    ],
  },
  payments: {
    title: "Zahlungen",
    subtitle: "Zahlungsaufträge erfassen und verfolgen",
    singular: "Zahlung",
    fields: [
      { key: "reference", label: "Referenz", required: true },
      { key: "recipient", label: "Empfänger", required: true },
      { key: "amount", label: "Betrag" },
      {
        key: "currency",
        label: "Währung",
        type: "select",
        options: ["EUR", "USD", "GBP", "CHF"],
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["Entwurf", "Ausstehend", "Ausgeführt", "Fehlgeschlagen"],
      },
    ],
  },
  customers: {
    title: "Kunden",
    subtitle: "Kundenstammdaten zentral verwalten",
    singular: "Kunde",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "company", label: "Unternehmen" },
      { key: "email", label: "E-Mail", type: "email" },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["Aktiv", "In Prüfung", "Inaktiv"],
      },
    ],
  },
  company: {
    title: "Unternehmen",
    subtitle: "Unternehmensprofil und Stammdaten",
    singular: "Unternehmensprofil",
    fields: [
      { key: "name", label: "Firmenname", required: true },
      { key: "email", label: "Kontakt-E-Mail", type: "email" },
      { key: "address", label: "Anschrift" },
      { key: "taxId", label: "Steuernummer / USt-ID" },
    ],
  },
  settings: {
    title: "Einstellungen",
    subtitle: "Globale Vorgaben für den Arbeitsbereich",
    singular: "Einstellung",
    fields: [
      { key: "name", label: "Bezeichnung", required: true },
      {
        key: "language",
        label: "Sprache",
        type: "select",
        options: ["Deutsch", "Englisch"],
      },
      {
        key: "currency",
        label: "Standardwährung",
        type: "select",
        options: ["EUR", "USD", "GBP", "CHF"],
      },
      {
        key: "notifications",
        label: "Benachrichtigungen",
        type: "select",
        options: ["Aktiv", "Deaktiviert"],
      },
    ],
  },
};

async function request(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options.headers,
    },
  });
  if (response.status === 204) return null;
  const body = await response.json();
  if (!response.ok) throw new Error(body.message || "Aktion fehlgeschlagen.");
  return body;
}

export default function OperationsPage({ category }) {
  const config = configurations[category];
  const empty = Object.fromEntries(
    config.fields.map((field) => [field.key, field.options?.[0] || ""]),
  );
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await request(`/api/workspace/${category}`);
      setRecords(body.records);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [category]);
  useEffect(() => {
    document.title = `${config.title} · CashFUX`;
    void load();
  }, [config.title, load]);
  const filtered = useMemo(
    () =>
      records.filter((record) =>
        JSON.stringify(record.data).toLowerCase().includes(query.toLowerCase()),
      ),
    [records, query],
  );
  const startCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
    setError(null);
  };
  const startEdit = (record) => {
    setEditing(record.id);
    setForm({ ...empty, ...record.data });
    setOpen(true);
    setError(null);
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const url = editing
        ? `/api/workspace/${category}/${editing}`
        : `/api/workspace/${category}`;
      await request(url, {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      setOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await request(`/api/workspace/${category}/${id}`, { method: "DELETE" });
      setRecords((current) => current.filter((record) => record.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <DashboardShell title={config.title} subtitle={config.subtitle}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[.22em] text-[#e01e37]">
            Verwaltung
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold">
            {config.title}
          </h1>
          <p className="mt-2 text-sm text-white/40">{config.subtitle}</p>
        </div>
        <button
          onClick={startCreate}
          className="flex h-11 items-center justify-center gap-2 bg-[#e01e37] px-5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" />
          {config.singular} anlegen
        </button>
      </div>
      <div className="mt-8 flex h-11 max-w-md items-center gap-3 border border-white/10 bg-[#07080d] px-4">
        <Search className="h-4 w-4 text-white/30" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Suchen …"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-white/25"
        />
      </div>
      {error && (
        <div className="mt-5 border-l-2 border-[#e01e37] bg-[#07080d] p-4 text-sm">
          {error}
        </div>
      )}
      {open && (
        <form
          onSubmit={save}
          className="mt-6 border border-white/10 bg-[#07080d] p-5 sm:p-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">
              {editing
                ? `${config.singular} bearbeiten`
                : `${config.singular} anlegen`}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/40"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {config.fields.map((field) => (
              <label key={field.key}>
                <span className="mb-2 block text-xs text-white/40">
                  {field.label}
                </span>
                {field.type === "select" ? (
                  <select
                    required={field.required}
                    value={form[field.key]}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    className="market-input"
                  >
                    {field.options.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    required={field.required}
                    type={field.type || "text"}
                    value={form[field.key]}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    className="market-input"
                  />
                )}
              </label>
            ))}
          </div>
          <button
            disabled={saving}
            className="mt-6 flex h-11 items-center gap-2 bg-[#e01e37] px-5 text-sm font-semibold disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Speichert …" : "Speichern"}
          </button>
        </form>
      )}
      <section className="mt-6 border border-white/10 bg-[#07080d]">
        <div className="border-b border-white/10 px-5 py-4 text-sm text-white/40">
          {filtered.length} {filtered.length === 1 ? "Eintrag" : "Einträge"}
        </div>
        {loading ? (
          <div className="p-8 text-sm text-white/35">
            Daten werden geladen …
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="font-display text-xl font-bold">
              Noch keine Einträge
            </p>
            <p className="mt-2 text-sm text-white/35">
              Lege den ersten Datensatz über die Schaltfläche oben an.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {filtered.map((record) => (
              <article
                key={record.id}
                className="grid gap-4 p-5 lg:grid-cols-[1fr_auto] lg:items-center"
              >
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {config.fields.map((field, index) => (
                    <div key={field.key}>
                      <p className="text-[10px] uppercase tracking-wider text-white/30">
                        {field.label}
                      </p>
                      <p
                        className={`mt-1 text-sm ${index === 0 ? "font-semibold" : "text-white/65"}`}
                      >
                        {record.data[field.key] || "–"}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(record)}
                    className="flex h-9 w-9 items-center justify-center border border-white/10 text-white/50 hover:text-white"
                    aria-label="Bearbeiten"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => void remove(record.id)}
                    className="flex h-9 w-9 items-center justify-center border border-white/10 text-white/50 hover:text-[#e01e37]"
                    aria-label="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </DashboardShell>
  );
}
